import React, { useState } from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Target, 
  BarChart3,
  PieChart,
  Calculator,
  CreditCard,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell } from 'recharts';

const FinancialDashboard: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('quarterly');
  const [selectedMetric, setSelectedMetric] = useState<any>(null);

  // Financial performance data
  const roiData = [
    { quarter: 'Q1 2023', roi: 180, investment: 2.5, returns: 4.5, efficiency: 85 },
    { quarter: 'Q2 2023', roi: 220, investment: 3.2, returns: 7.0, efficiency: 88 },
    { quarter: 'Q3 2023', roi: 195, investment: 4.1, returns: 8.0, efficiency: 82 },
    { quarter: 'Q4 2023', roi: 340, investment: 2.8, returns: 9.5, efficiency: 95 },
    { quarter: 'Q1 2024', roi: 285, investment: 3.5, returns: 10.0, efficiency: 91 },
    { quarter: 'Q2 2024', roi: 320, investment: 4.2, returns: 13.4, efficiency: 94 },
  ];

  const costBreakdownData = [
    { category: 'Technology', amount: 4.2, percentage: 35, color: '#3B82F6' },
    { category: 'Personnel', amount: 3.8, percentage: 32, color: '#10B981' },
    { category: 'Marketing', amount: 2.1, percentage: 18, color: '#F59E0B' },
    { category: 'Operations', amount: 1.2, percentage: 10, color: '#8B5CF6' },
    { category: 'Other', amount: 0.6, percentage: 5, color: '#EF4444' },
  ];

  const budgetVarianceData = [
    { department: 'Product', budget: 2.5, actual: 2.3, variance: -8 },
    { department: 'Engineering', budget: 4.2, actual: 4.5, variance: 7 },
    { department: 'Marketing', budget: 2.1, actual: 1.9, variance: -10 },
    { department: 'Sales', budget: 1.8, actual: 2.1, variance: 17 },
    { department: 'Operations', budget: 1.2, actual: 1.1, variance: -8 },
  ];

  // Key financial metrics
  const financialMetrics = [
    {
      id: 'capital_productivity',
      name: 'Capital Productivity Index',
      value: 94,
      change: 8.2,
      positive: true,
      description: 'Efficiency of funding conversion to business value',
      target: 90,
      unit: '%',
      amount: '£13.4M'
    },
    {
      id: 'roi_tracker',
      name: 'Portfolio ROI',
      value: 320,
      change: 12.3,
      positive: true,
      description: 'Return on investment across all initiatives',
      target: 250,
      unit: '%',
      amount: '£9.2M'
    },
    {
      id: 'cost_per_impact',
      name: 'Cost per Impact',
      value: 0.18,
      change: -15.2,
      positive: true,
      description: 'Cost efficiency of business impact generation',
      target: 0.25,
      unit: '£/point',
      amount: '£2.1M'
    },
    {
      id: 'budget_efficiency',
      name: 'Budget Efficiency',
      value: 87,
      change: 5.1,
      positive: true,
      description: 'Strategic alignment of budget allocation',
      target: 85,
      unit: '%',
      amount: '£11.8M'
    }
  ];

  // Financial insights and alerts
  const financialInsights = [
    {
      id: 1,
      type: 'opportunity',
      title: 'AI Investment ROI Exceeding Projections',
      description: 'AI personalisation initiative delivering 340% ROI vs 250% target',
      impact: 'high',
      value: '£6.8M',
      action: 'Increase AI investment allocation by 25%',
      timeframe: 'This quarter'
    },
    {
      id: 2,
      type: 'concern',
      title: 'Marketing Spend Efficiency Declining',
      description: 'Customer acquisition costs increased 18% while conversion rates dropped',
      impact: 'medium',
      value: '£420K',
      action: 'Review marketing channel effectiveness',
      timeframe: 'Last month'
    },
    {
      id: 3,
      type: 'success',
      title: 'Operational Cost Optimization Success',
      description: 'Process automation reduced operational costs by 22%',
      impact: 'high',
      value: '£1.2M',
      action: 'Scale automation to additional processes',
      timeframe: 'Last quarter'
    }
  ];

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'opportunity': return <ArrowUpRight className="w-5 h-5 text-green-500" />;
      case 'concern': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-blue-500" />;
      default: return <BarChart3 className="w-5 h-5 text-gray-500" />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'opportunity': return 'border-green-600 bg-green-900';
      case 'concern': return 'border-red-600 bg-red-900';
      case 'success': return 'border-blue-600 bg-blue-900';
      default: return 'border-gray-600 bg-gray-900';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center space-x-3">
            <DollarSign className="w-8 h-8 text-green-400" />
            <span>Financial Performance Dashboard</span>
          </h1>
          <p className="text-gray-400">ROI analysis, cost optimization, and financial health metrics</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none"
          >
            <option value="monthly">Monthly View</option>
            <option value="quarterly">Quarterly View</option>
            <option value="yearly">Yearly View</option>
          </select>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Live Financial Data</span>
          </div>
        </div>
      </div>

      {/* Key Financial Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {financialMetrics.map((metric) => (
          <div 
            key={metric.id} 
            className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-gray-600 transition-colours cursor-pointer"
            onClick={() => setSelectedMetric(metric)}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-300">{metric.name}</h3>
              {metric.positive ? (
                <TrendingUp className="w-5 h-5 text-green-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
            </div>
            <div className="flex items-baseline space-x-2 mb-2">
              <span className="text-2xl font-bold text-white">{metric.value}</span>
              <span className="text-sm text-gray-400">{metric.unit}</span>
            </div>
            <div className="flex items-center justify-between mb-3">
              <span className={`text-sm ${metric.positive ? 'text-green-500' : 'text-red-500'}`}>
                {metric.positive ? '+' : ''}{metric.change}%
              </span>
              <span className="text-sm font-medium text-green-400">{metric.amount}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${metric.value >= metric.target ? 'bg-green-500' : 'bg-yellow-500'}`}
                style={{ width: `${Math.min((metric.value / (metric.target * 1.2)) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      {/* ROI Analysis & Cost Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ROI Trend Analysis */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Target className="w-5 h-5 text-green-400" />
            <span>ROI Trend Analysis</span>
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={roiData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="quarter" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Area type="monotone" dataKey="roi" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
              <Area type="monotone" dataKey="efficiency" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Cost Breakdown */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <PieChart className="w-5 h-5 text-blue-400" />
            <span>Cost Distribution</span>
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={costBreakdownData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                dataKey="amount"
                label={({ category, percentage }) => `${category}: ${percentage}%`}
                labelLine={false}
              >
                {costBreakdownData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Budget Variance Analysis */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
          <Calculator className="w-5 h-5 text-purple-400" />
          <span>Budget vs Actual Analysis</span>
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={budgetVarianceData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="department" stroke="#9CA3AF" />
            <YAxis stroke="#9CA3AF" />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1F2937',
                border: '1px solid #374151',
                borderRadius: '8px',
                color: '#F9FAFB'
              }}
            />
            <Bar dataKey="budget" fill="#6B7280" name="Budget (£M)" />
            <Bar dataKey="actual" fill="#3B82F6" name="Actual (£M)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Financial Insights & Investment Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Financial Insights */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-indigo-400" />
            <span>Financial Insights</span>
          </h3>
          <div className="space-y-4">
            {financialInsights.map((insight) => (
              <div key={insight.id} className={`p-4 rounded-lg border ${getInsightColor(insight.type)}`}>
                <div className="flex items-start space-x-3">
                  {getInsightIcon(insight.type)}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{insight.title}</h4>
                      <span className="text-sm font-bold text-green-400">{insight.value}</span>
                    </div>
                    <p className="text-sm text-gray-300 mb-3">{insight.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">{insight.timeframe}</span>
                      <button className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours">
                        {insight.action}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Investment Portfolio Performance */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Wallet className="w-5 h-5 text-yellow-400" />
            <span>Investment Portfolio</span>
          </h3>
          <div className="space-y-4">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-medium">AI & Machine Learning</h4>
                <span className="text-green-400 font-bold">+340% ROI</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Investment: £2.8M</span>
                <span className="text-white">Returns: £9.5M</span>
              </div>
              <div className="mt-2 w-full bg-gray-600 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '95%' }}></div>
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-medium">Mobile Platform</h4>
                <span className="text-green-400 font-bold">+285% ROI</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Investment: £3.5M</span>
                <span className="text-white">Returns: £10.0M</span>
              </div>
              <div className="mt-2 w-full bg-gray-600 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '88%' }}></div>
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-medium">Infrastructure</h4>
                <span className="text-blue-400 font-bold">+195% ROI</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Investment: £4.1M</span>
                <span className="text-white">Returns: £8.0M</span>
              </div>
              <div className="mt-2 w-full bg-gray-600 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: '75%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cash Flow & Liquidity Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <CreditCard className="w-6 h-6 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Operating Cash Flow</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">£18.4M</div>
          <div className="text-sm text-green-400">+12.3% vs last quarter</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Wallet className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Free Cash Flow</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">£14.2M</div>
          <div className="text-sm text-green-400">+8.7% vs last quarter</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Calculator className="w-6 h-6 text-purple-400" />
            <h3 className="text-lg font-semibold text-white">EBITDA Margin</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">28.5%</div>
          <div className="text-sm text-green-400">+2.1% vs last quarter</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Target className="w-6 h-6 text-yellow-400" />
            <h3 className="text-lg font-semibold text-white">Burn Rate</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">£2.1M</div>
          <div className="text-sm text-red-400">+5.2% vs last month</div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;